import { memo, MouseEventHandler, useRef } from "react";
import { Icon, IconType } from "../../primitives/icon";
import { cn } from "../../utils/classes";

export function getInitials(name: string): string {
  const chunks = (name ?? "")?.replace(/['"]/gu, "").split(/\s+/);
  let initials = "";

  for (let i = 0; i < chunks.length && initials.length < 2; i++) {
    const chars = Array.from(chunks[i]);
    if (chars[0]) {
      initials += chars[0];
    }
  }

  return initials.toUpperCase();
}

type BaseAvatarProps = {
  size?: "lg" | "md" | "sm" | "xs";
  src?: string | null;
  className?: string;
  onClick?: MouseEventHandler<HTMLImageElement> | undefined;
  fallbackIcon?: IconType;
};

type CompanyAvatarProps = BaseAvatarProps & {
  type: "company";
  fallbackImageSrc: string | null;
  initials?: never;
};

type UserAvatarProps = BaseAvatarProps & {
  type: "user";
  initials?: string | null;
  fallbackImageSrc?: never;
};

type AvatarProps = CompanyAvatarProps | UserAvatarProps;

function getColorFromInitials(initials?: string | null): string {
  if (!initials || initials.length === 0) {
    return "var(--gray-100)";
  }

  const chars = initials.slice(0, 2).padEnd(2, initials[0] || "A");
  const firstChar = chars.charCodeAt(0);
  const secondChar = chars.charCodeAt(1);

  const hash = (firstChar * 31 + secondChar * 17) % 360;
  const hue = Math.abs(hash);

  return `hsl(${hue}, 40%, 90%)`;
}

const FallbackAvatar = (
  props: Pick<AvatarProps, "type" | "initials" | "size" | "fallbackIcon">,
) => {
  const { size } = props;
  if (props.type === "user" && props.initials) {
    return (
      <span
        className={cn(
          "font-semibold leading-none",
          size === "lg" && "text-2xl",
          size === "md" && "text-lg",
          size === "sm" && "text-sm",
          size === "xs" && "text-xs",
          "size-full flex items-center justify-center",
        )}
        style={{ backgroundColor: getColorFromInitials(props.initials) }}
      >
        {props.initials}
      </span>
    );
  }

  return (
    <Icon
      type={props.fallbackIcon ?? (props.type === "user" ? "User" : "Company")}
      className={cn(
        "text-gray-900",
        size === "lg" && "size-6",
        size === "md" && "size-4",
        size === "sm" && "size-3.5",
      )}
    />
  );
};

export const AvatarV2 = memo(
  ({ size = "md", src, className, onClick, ...props }: AvatarProps) => {
    const fallbackImgRef = useRef<HTMLImageElement>(null);

    const handleFallbackImageLoad = () => {
      if (fallbackImgRef.current) {
        const { naturalWidth, naturalHeight } = fallbackImgRef.current;
        const threshold = 24; // usually google favicons are 16x16 so we consider everything smaller than 24x24
        const isSmall = naturalWidth < threshold || naturalHeight < threshold;
        if (isSmall) {
          fallbackImgRef.current.classList.add("hidden");
        }
      }
    };

    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden border border-gray-300 bg-white",
          size === "lg" && "size-16 rounded-sm",
          size === "md" && "size-10 rounded-xs",
          size === "sm" && "size-8 rounded-xxs",
          size === "xs" && "size-6 rounded-[2px]",
          onClick && "cursor-pointer hover:bg-gray-100 hover:opacity-70",
          "overflow-hidden relative",
          className,
        )}
        onClick={onClick}
      >
        {src && (
          <img
            alt={`${props.type} image`}
            className={"size-full peer/primary"}
            src={src}
            onError={(e) => e.currentTarget.classList.add("hidden")}
          />
        )}
        {props.type === "company" && props.fallbackImageSrc && (
          <img
            ref={fallbackImgRef}
            alt={`${props.type} fallback image`}
            className={
              "absolute size-full peer/fallback peer-[:not(.hidden)]/primary:hidden"
            }
            src={props.fallbackImageSrc!}
            onLoad={handleFallbackImageLoad}
            onError={(e) => e.currentTarget.classList.add("hidden")}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center peer-[:not(.hidden)]/primary:hidden peer-[:not(.hidden)]/fallback:hidden">
          <FallbackAvatar
            type={props.type}
            initials={props.initials}
            size={size}
            fallbackIcon={props.fallbackIcon}
          />
        </div>
      </div>
    );
  },
);

AvatarV2.displayName = "AvatarV2";

export const getCompanyImageUrl = (websiteUrl?: string | null) => {
  if (!websiteUrl) {
    return null;
  }

  return `https://www.google.com/s2/favicons?sz=128&domain=${websiteUrl}`;
};
